// The leads as regions over a drawn ECG sheet: one cell per lead, placed in percentages of the sheet so they stay on
// their traces at any width. Used by the lead grid (tap a lead, v2.2) and by the 12-lead paper (the right leads marked
// at the reveal, on the stage and in classroom mode). Buttons when a player can tap; quiet marks when `decorative`.
import type { CSSProperties } from 'react';
import type { Lead } from './types';
import { LEADS } from './synth';
import type { Cell } from './paper';
import type { SlipRowState } from '../BloodsSlip';

export interface LeadRegion { /** The answer option id. */ id: string; lead: Lead }
export interface LeadPick {
  regions: ReadonlyArray<LeadRegion>;
  stateOf: (id: string) => SlipRowState;
  disabled?: boolean;
  onPick?: (id: string) => void;
  /** At the reveal: the share of the room that tapped each lead. */
  pctOf?: (id: string) => number;
  /** The option this player tapped. */
  mine?: string;
}

const pct = (v: number) => `${(100 * v).toFixed(4)}%`;

function Tick() {
  return <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 12.8l4.6 4.6L19.5 7" /></svg>;
}
function Cross() {
  return <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="3.6" strokeLinecap="round"><path d="M6.5 6.5l11 11M17.5 6.5l-11 11" /></svg>;
}

export interface LeadButtonsProps {
  cells: Record<Lead, Cell>;
  /** The sheet's size in paper pixels. */
  width: number; height: number;
  pick?: LeadPick;
  revealed?: boolean;
  /** Print each lead's name on its cell (the lead grid); the 12-lead paper prints its own. */
  names?: boolean;
  /** Which column each lead sits in, for placing the name clear of the calibration pulse. */
  columnOf?: (lead: Lead) => number;
  /** Marks only, for a small or broadcast sheet: hidden from screen readers and never tappable. */
  decorative?: boolean;
}

export function LeadButtons({ cells, width, height, pick, revealed = false, names = true, columnOf, decorative = false }: LeadButtonsProps) {
  const byLead = new Map((pick?.regions ?? []).map((r) => [r.lead, r]));
  return (
    <>
      {LEADS.map((lead) => {
        const c = cells[lead];
        if (!c) return null;
        const col = columnOf ? columnOf(lead) : undefined;
        const style: CSSProperties = { left: pct(c.x / width), top: pct(c.y / height), width: pct(c.w / width), height: pct(c.h / height) };
        const region = byLead.get(lead);
        const name = names ? <span className="rx-lead-name">{lead}</span> : null;
        if (!pick || !region) {
          return names ? <span key={lead} className="rx-lead" data-col={col} data-still="" style={style} aria-hidden="true">{name}</span> : null;
        }
        const state = pick.stateOf(region.id);
        const raw = revealed && pick.pctOf ? pick.pctOf(region.id) : undefined;
        // Nobody tapped it: said aloud, but only printed on a right lead, so the sheet is not covered in zeros.
        const share = raw !== undefined && (raw > 0 || state === 'correct') ? raw : undefined;
        const mine = pick.mine === region.id;
        const marks = (
          <>
            {name}
            {share !== undefined && <span className="rx-lead-pct">{share}%</span>}
            {revealed && (state === 'correct' || state === 'wrong') && (
              <span className="rx-lead-mark" data-tone={state}>{state === 'correct' ? <Tick /> : <Cross />}</span>
            )}
            {revealed && mine && <span className="rx-lead-you" data-tone={state}>You</span>}
          </>
        );
        if (decorative) {
          return <span key={lead} className="rx-lead" data-col={col} data-mark="" data-state={state} data-mine={mine ? '' : undefined} style={style} aria-hidden="true">{marks}</span>;
        }
        const said = `Lead ${lead}.${revealed && state === 'correct' ? ' A right answer.' : ''}${mine ? (revealed ? ' Your pick.' : ' Locked in.') : ''}${raw !== undefined ? ` ${raw}% of the room.` : ''}`;
        return (
          <button key={lead} type="button" className="rx-lead" data-col={col} style={style} data-option={region.id} data-state={state} data-mine={mine ? '' : undefined}
            aria-label={said} aria-pressed={state === 'locked' || (mine && revealed)} disabled={pick.disabled} onClick={() => pick.onPick?.(region.id)}>
            {marks}
          </button>
        );
      })}
    </>
  );
}
